// src/components/Employees.js

import React, { useCallback, useContext, useEffect, useState } from 'react'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'

import { ApplicationContext } from 'context'

import Loading from 'components/Loading'
import ConfirmModal from 'components/ConfirmModal'
import AlertModal from 'components/AlertModal'
import ButtonPrimary from 'components/ButtonPrimary'
import FullScreenSlideout from 'components/FullScreenSlideout'

import EditEmployee from './EditEmployee'
import Modal from './Modal'

import api from 'lib/api'
import { formatPhoneNumber, discectError } from 'lib/utils'

import styles from './styles.module.scss'

const Employees = () => {
  const { contextAgency, contextError, contextLoading } = useContext(ApplicationContext)
  const [isLoading, setIsLoading] = contextLoading // eslint-disable-line
  const [error, setError] = contextError // eslint-disable-line
  const [agency] = contextAgency // eslint-disable-line
  
  const [modalError, setModalError] = useState(null)
  const [editUser, setEditUser] = useState(null)
  const [isAddingUser, setIsAddingUser] = useState(false)
  const [users, setUsers] = useState(false)
  const [filter, setFilter] = useState({ search: '' })
  const [confirm, setConfirm] = useState({ display: false, name: null, description: null, meta: null })
  const [alert, setAlert] = useState({ display: false, title: null, text: null })

  const _fetchUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      const { employees } = await api.employees.all()
      setUsers(() => employees)
      setIsLoading(false)
      return employees
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }, [setError, setIsLoading])
  
  useEffect(() => {
    if (!users) _fetchUsers()
  }, [users, _fetchUsers])

  const syncEmployees = async (users = null) => {
    setError(null)
    try {
      if (!users) users = await _fetchUsers()
      const inventoryIds = users.map(itm => {
        return {
          id: itm.id,
          authZeroId: itm.authZeroId,
          nameFirst: itm.nameFirst,
          nameLast: itm.nameLast,
        }
      })
      if (inventoryIds?.length) {
        await api.inventory.syncEmployees(agency.id, { employees: inventoryIds })
      }
    } catch(e) {
      setError(discectError(e))
    }
  }

  const _onSaveNewUser = async data => {
    setError(null)
    setIsLoading(true)
    setIsAddingUser(false)
    try {    
      const results = await api.employees.createEmployee(data)
      if (results) {
        const users = await _fetchUsers()
        await syncEmployees(users)
        setAlert({ display: true, title: 'Success!', text: 'You have Added a new User. They should receive an email shortly.' })
      }
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _onUpdateEmployee = async data => {
    setModalError(null)
    setIsLoading(true)
    try {    
      const foundUser = users.find(itm => itm.id === editUser)
      if (foundUser) {
        const results = await api.employees.updateEmployee(foundUser.id, data)
        if (results) {
          const users = await _fetchUsers()
          await syncEmployees(users)
          setAlert({ display: true, title: 'Success!', text: 'You have updated the User.' })
        }
      } else {
        throw new Error('User not found')
      }
    } catch(e) {
      console.log(e)
      setModalError(discectError(e))
      setIsLoading(false)
    }
  }

  const _deleteUser = async id => {
    setModalError(null)
    setIsLoading(true)
    try {    
      const foundUser = users.find(itm => itm.id === id)
      if (foundUser) {
        const results = await api.employees.deleteEmployee(foundUser.id)
        if (results) {
          const users = await _fetchUsers()
          await syncEmployees(users)
          setAlert({ display: true, title: 'Success!', text: 'You have deleted/disabled the User.' })
        }
      } else {
        throw new Error('User not found')
      }
    } catch(e) {
      setModalError(discectError(e))
      setIsLoading(false)
    }
  }

  const _resetUser = async id => {
    setError(null)
    setIsLoading(true)
    try {    
      const foundUser = users.find(itm => itm.id === id)
      if (foundUser) {
        const results = await api.employees.resetEmployee(foundUser.id)
        if (results) {
          const users = await _fetchUsers()
          await syncEmployees(users)
          setAlert({ display: true, title: 'Success!', text: 'You have reset the User.' })
        }
      } else {
        throw new Error('User not found')
      }
    } catch(e) {
      setError(discectError(e))
      setIsLoading(false)
    }
  }

  const _confirmAction = async () => {
    const { meta, type } = confirm
    setConfirm({ display: false, name: null, description: null, meta: null })

    setIsLoading(true)
    try {
      if (type === 'DELETE_USER') {
        await _deleteUser(meta.id)
      } else if (type === 'RESET_USER') {
        await _resetUser(meta.id)
      } 
    } catch(e) {
      console.error(e)
      setAlert({ display: true, title: 'Error!', text: 'There was a problem with your request.' })
    }
    setIsLoading(false)
  }

  const _onResetUser = () => {
    const confirm = {
      type: 'RESET_USER',
      title: "Reset User, Are you sure?",
      description: "By continuing, we will email/text the user and have them setup a new password?",
      meta: { id: editUser }
    }
    setConfirm(confirm)
  }

  const _onDisableUser = () => {
    const confirm = {
      type: 'DELETE_USER',
      title: "Delete User, Are you sure?",
      description: "Please verify that you would like to disable this Bail Bond Agent.  They will no longer be able to access Simply Bail and you will need to reach out to Simply Bail if this is a mistake.",
      meta: { id: editUser }
    }
    setConfirm(confirm)
  }

  // Layout

  const _getIsTrainedClass = (user) => {
    if (user.isTrained) {
      return styles.isTrained
    }
    return styles.isTrained
  }

  const _getNotificationOption = ({ enabled, types: { email, text }}, type) => {
    if (!enabled) return <strong>OFF</strong>
    if ((type === 'email' && email) || (type === 'text' && text)) {
      return <strong className={styles.notificationOn}>ON</strong>
    }
    return <strong>OFF</strong>
  }

  const _getFlyout = () => {
    const foundUser = users.find(itm => itm.id === editUser)
    return (
      <FullScreenSlideout 
        isMasked
        isViewClose
        title="Edit User"
        isOpen={foundUser}
        setIsOpen={setEditUser}
      >
        {foundUser && (
          <EditEmployee 
            bondsman={agency}
            user={foundUser}
            onUpdateUser={_onUpdateEmployee}
            onResetUser={_onResetUser}
            onDeleteUser={_onDisableUser}
            onClose={() => setEditUser(false)}
          />
        )}
      </FullScreenSlideout>
    )
  }

  const _getFilteredUsers = () => {
    return users
      .filter(itm => {
        if (filter.search !== '') {
          return (
            itm.nameFirst.toLowerCase().includes(filter.search.toLowerCase()) || 
            itm.nameLast.toLowerCase().includes(filter.search.toLowerCase()) || 
            itm.email.toLowerCase().includes(filter.search.toLowerCase())
          )
        }
        return true
      })
  }

  // Alert & Confirm Actions

  const _confirmBack = () => {
    setConfirm({ display: false, name: null, description: null, meta: null })
  }

  const _onAlertClose = () => {
    setAlert({ display: false, title: null, text: null })
  }

  if (users) {
    return (
      <>
        <div className={styles.users__filters}>
          <div className={styles.users__filters__left}>
            <form onSubmit={e => e.preventDefault()} className={styles.searchForm}>
              <input type="search" placeholder={"Enter the Users Name"} value={filter?.search || ''} onChange={e => setFilter(f => { return { ...f, search: e.target.value } })} />
              <button className="butt butt--teal" type="submit"><i className="fas fa-search"></i></button>
            </form>
          </div>
          <div className={styles.users__filters__right}>
            {/* <ButtonPrimary small color="blue" icon={faRefresh} onClick={() => syncEmployees()}>Sync</ButtonPrimary> */}
            <ButtonPrimary small color="navy" icon={faPlusCircle} onClick={() => setIsAddingUser(true)}>Add User</ButtonPrimary>
          </div>
        </div>
        <div className={styles.users__container}>
          <div className={styles.users}>
            <table>
              <thead>
                <tr>
                  <th>Name & Testing</th>
                  <th>Commuications</th>
                  <th>Permissions</th>
                  <th>Notifications</th>
                </tr>
              </thead>
              <tbody>
                {users?.length > 0 && _getFilteredUsers().map((itm, key) => {
                  return (
                    <tr key={`employee_${key}`} onClick={() => {
                      if (itm.type !== 'SUPER') setEditUser(itm.id)
                    }}>
                      <td>
                        <div className={`${styles.tableCell} ${styles.tableCell__name}`}>
                          <h3 className={_getIsTrainedClass(itm)}>{`${itm.nameFirst} ${itm.nameLast}`}</h3>
                          {/* <p>Test Name: <strong>{itm.testName}</strong></p> */}
                          {/* {itm.isTrainingMode && <span className={styles.trainingMode}>Training Mode</span>} */}
                        </div>
                      </td>
                      <td>
                        <div className={styles.tableCell}>
                          <h4>{itm.email}</h4>
                          <p>Tel: <strong>{formatPhoneNumber(itm.phone)}</strong></p>
                        </div>
                      </td>
                      <td>
                        <div className={styles.tableCell}>
                          <h4>{itm.permissions.length} Set Permissions</h4>
                          <p><strong>{itm.type === 'SUPER' ? 'SIMPLY BAIL' : itm.type}</strong></p>
                        </div>
                      </td>
                      <td>
                        <div className={`${styles.tableCell} ${styles.tableCell__notifications}`}>
                          <h4>
                            <span>Email: {_getNotificationOption(itm.notifications, 'email')}</span>
                            <span>Text: {_getNotificationOption(itm.notifications, 'text')}</span>
                          </h4>
                          {/* <p>
                            Slug: <strong>{itm.slug}</strong>
                          </p> */}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {_getFlyout()}
            {isAddingUser && <Modal error={modalError} bondsman={agency} onSave={_onSaveNewUser} onClose={() => setIsAddingUser(false)} />}
            <AlertModal title={alert.title} text={alert.text} isOn={alert.display} onClose={_onAlertClose} />
            {confirm.type && (
              <ConfirmModal 
                title={confirm.title}
                text={confirm.description}
                onConfirm={_confirmAction}
                onCancel={_confirmBack} />
            )}
            {isLoading && <Loading text="Please wait" />}
          </div>
        </div>
      </>
    )
  }
  return null
}

export default Employees